import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { cn } from "@arcadeai/design-system/lib/utils";
import {
  ArrowDown,
  ArrowRight,
  Check,
  Eye,
  Filter,
  Globe,
  LayoutDashboard,
  Monitor,
  Play,
  Terminal,
  Zap,
} from "lucide-react";
import {
  HOOK_POINT_INFO,
  type HookPoint,
  type HookWithPlugin,
} from "../../types";
import { sortHooksByPriority } from "../../utils";

type HookExecutionPipelineProps = {
  hooks: HookWithPlugin[];
  /** Optional: highlight hooks that would be added (for preview in modal) */
  pendingHooks?: Array<{
    hookPoint: HookPoint;
    priority: number;
    phase: "before" | "after";
    binding?: "tenant" | "project";
    name?: string;
  }>;
  /** Compact mode for modal preview */
  compact?: boolean;
  /** Plugin ID to highlight (when hovering in table) */
  highlightedPluginId?: string | null;
  /** Callback when a hook is clicked to view plugin details */
  onSelectPlugin?: (pluginId: string) => void;
};

const HOOK_POINT_ORDER: HookPoint[] = ["tool.access", "tool.pre", "tool.post"];

// Stage accent colors: Access (amber), Pre (blue), Post (purple) — matches table endpoint badges
const STAGE_COLORS: Record<
  HookPoint,
  { border: string; bg: string; text: string; icon: string }
> = {
  "tool.access": {
    border: "border-amber-500/50",
    bg: "bg-amber-500/10",
    text: "text-amber-600",
    icon: "text-amber-600",
  },
  "tool.pre": {
    border: "border-blue-500/50",
    bg: "bg-blue-500/10",
    text: "text-blue-600",
    icon: "text-blue-600",
  },
  "tool.post": {
    border: "border-purple-500/50",
    bg: "bg-purple-500/10",
    text: "text-purple-600",
    icon: "text-purple-600",
  },
};

const HookPointIcon = ({
  hookPoint,
  className,
}: {
  hookPoint: HookPoint;
  className?: string;
}) => {
  const iconClass = cn("size-4", className);
  switch (hookPoint) {
    case "tool.access":
      return <Eye className={iconClass} />;
    case "tool.pre":
      return <Play className={iconClass} />;
    case "tool.post":
      return <Filter className={iconClass} />;
    default:
      return <Zap className={iconClass} />;
  }
};

type HookNodeProps = {
  hook: HookWithPlugin;
  index: number;
  isLast: boolean;
  compact?: boolean;
  isHighlighted?: boolean;
  onSelect?: (pluginId: string) => void;
};

const HookNode = ({
  hook,
  index,
  compact,
  isHighlighted,
  onSelect,
}: HookNodeProps) => {
  const isActive = hook.status === "active";
  const bindingType = hook.plugin?.binding.type;
  const isOrg = bindingType === "tenant";

  // Binding-based colors: purple for org, pink for project
  const bindingColors = isOrg
    ? "border-l-purple-500 bg-purple-500/5"
    : "border-l-pink-500 bg-pink-500/5";

  const indexColors = isOrg
    ? "bg-purple-500/20 text-purple-400"
    : "bg-pink-500/20 text-pink-400";

  // Highlight styles when system is hovered in table
  const getHighlightStyles = () => {
    if (!isHighlighted) {
      return "";
    }
    if (isOrg) {
      return "ring-2 ring-purple-500 ring-offset-1 ring-offset-background bg-purple-500/15 scale-[1.02]";
    }
    return "ring-2 ring-pink-500 ring-offset-1 ring-offset-background bg-pink-500/15 scale-[1.02]";
  };
  const highlightStyles = getHighlightStyles();

  const handleClick = () => {
    if (onSelect && hook.plugin_id) {
      onSelect(hook.plugin_id);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              className={cn(
                "flex min-w-0 cursor-pointer items-center gap-1.5 text-left transition-all duration-200",
                isHighlighted && "z-10"
              )}
              onClick={handleClick}
              type="button"
            />
          }
        >
          <span
            className={cn(
              "flex size-5 shrink-0 items-center justify-center rounded-full font-medium text-xs transition-all duration-200",
              isActive ? indexColors : "bg-muted text-muted-foreground",
              isHighlighted && "scale-110",
              compact && "size-4 text-[10px]"
            )}
          >
            {index + 1}
          </span>
          <div
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-md border border-l-2 px-2 py-1.5 transition-all duration-200",
              isActive
                ? cn("border-border", bindingColors)
                : "border-muted bg-muted/30 opacity-50",
              highlightStyles,
              compact && "gap-1.5 px-1.5 py-1"
            )}
          >
            <span
              className={cn(
                "min-w-0 flex-1 truncate font-medium text-xs",
                compact && "text-[11px]"
              )}
            >
              {hook.plugin?.name ?? hook.name}
            </span>
            <Badge
              className={cn(
                "shrink-0 px-1.5 py-0 text-[9px]",
                isActive &&
                  (isOrg
                    ? "border-purple-500/50 text-purple-400"
                    : "border-pink-500/50 text-pink-400")
              )}
              variant="outline"
            >
              {isOrg ? "Org" : "Project"}
            </Badge>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <div className="flex flex-col gap-1">
            <div className="font-medium">{hook.plugin?.name ?? hook.name}</div>
            <div className="text-muted-foreground text-xs">
              {`Scope: ${isOrg ? "Organization" : "Project"}`}
            </div>
            <div className="text-muted-foreground text-xs">
              Priority: {hook.priority} | Phase: {hook.phase}
            </div>
            <div className="text-muted-foreground text-xs">
              Status: {hook.status}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

type PendingHookNodeProps = {
  pending: {
    hookPoint: HookPoint;
    priority: number;
    phase: "before" | "after";
    binding?: "tenant" | "project";
    name?: string;
  };
  index: number;
  isLast: boolean;
  compact?: boolean;
};

const PendingHookNode = ({ pending, index, compact }: PendingHookNodeProps) => {
  const isOrg = pending.binding === "tenant";

  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full bg-primary font-medium text-primary-foreground text-xs",
          compact && "size-4 text-[10px]"
        )}
      >
        {index + 1}
      </span>
      <div
        className={cn(
          "flex flex-1 items-center gap-2 rounded-md border-2 border-primary border-dashed bg-primary/5 px-3 py-1.5",
          compact && "px-2 py-1"
        )}
      >
        <span
          className={cn(
            "truncate font-medium text-brand-text text-sm",
            compact && "text-xs"
          )}
        >
          {pending.name ?? "New Hook"}
        </span>
        <Badge className="shrink-0 text-[10px]" variant="default">
          P{pending.priority}
        </Badge>
        <Badge
          className={cn(
            "shrink-0 text-[10px]",
            isOrg
              ? "border-violet-500/50 bg-violet-500/10 text-violet-700"
              : "border-pink-500/50 bg-pink-500/10 text-pink-700"
          )}
          variant="outline"
        >
          {isOrg ? "Org" : "Project"}
        </Badge>
      </div>
    </div>
  );
};

type HookPointSectionProps = {
  hookPoint: HookPoint;
  hooks: HookWithPlugin[];
  pendingHooks?: HookExecutionPipelineProps["pendingHooks"];
  compact?: boolean;
  highlightedPluginId?: string | null;
  onSelectPlugin?: (pluginId: string) => void;
};

// Helper to get phase order (matching engine's execution order)
// Order: tenant:before(1) → project:before(2) → project:after(3) → tenant:after(4)
const getPhaseOrder = (
  binding?: "tenant" | "project",
  phase?: "before" | "after"
) => {
  if (binding === "tenant") {
    if (phase === "before") {
      return 1;
    }
    return 4;
  }
  if (binding === "project") {
    if (phase === "before") {
      return 2;
    }
    return 3;
  }
  return 99;
};

type DisplayItem =
  | { type: "hook"; hook: HookWithPlugin }
  | {
      type: "pending";
      pending: NonNullable<HookExecutionPipelineProps["pendingHooks"]>[0];
    };

// Returns true if pending should come before hook
const shouldPendingComeBefore = (
  pending: NonNullable<HookExecutionPipelineProps["pendingHooks"]>[0],
  hook: HookWithPlugin
): boolean => {
  const hookOrder = getPhaseOrder(
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    hook.plugin?.binding?.type as "tenant" | "project" | undefined,
    hook.phase
  );
  const pendingOrder = getPhaseOrder(pending.binding, pending.phase);

  if (pendingOrder !== hookOrder) {
    return pendingOrder < hookOrder;
  }
  return pending.priority < hook.priority;
};

// Helper to merge and sort hooks with pending hooks
const mergeHooksInOrder = (
  sortedHooks: HookWithPlugin[],
  pendingAtPoint: NonNullable<HookExecutionPipelineProps["pendingHooks"]>
): DisplayItem[] => {
  // Sort pending hooks by phase order, then by priority (do not mutate original)
  const sortedPending = [...pendingAtPoint].sort((a, b) => {
    const aOrder = getPhaseOrder(a.binding, a.phase);
    const bOrder = getPhaseOrder(b.binding, b.phase);
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.priority - b.priority;
  });

  const displayItems: DisplayItem[] = [];
  let hookIndex = 0;
  let pendingIndex = 0;

  while (
    hookIndex < sortedHooks.length ||
    pendingIndex < sortedPending.length
  ) {
    const hook = sortedHooks[hookIndex];
    const pending = sortedPending[pendingIndex];

    if (!pending) {
      displayItems.push({ type: "hook", hook });
      hookIndex += 1;
    } else if (!hook) {
      displayItems.push({ type: "pending", pending });
      pendingIndex += 1;
    } else if (shouldPendingComeBefore(pending, hook)) {
      displayItems.push({ type: "pending", pending });
      pendingIndex += 1;
    } else {
      displayItems.push({ type: "hook", hook });
      hookIndex += 1;
    }
  }

  return displayItems;
};

const HookPointSection = ({
  hookPoint,
  hooks,
  pendingHooks,
  compact,
  highlightedPluginId,
  onSelectPlugin,
}: HookPointSectionProps) => {
  const info = HOOK_POINT_INFO[hookPoint];
  const sortedHooks = sortHooksByPriority(hooks);

  const pendingAtPoint = (
    pendingHooks?.filter((p) => p.hookPoint === hookPoint) ?? []
  ).sort((a, b) => {
    // Sort by phase order (binding + phase), then by priority
    const aOrder = getPhaseOrder(a.binding, a.phase);
    const bOrder = getPhaseOrder(b.binding, b.phase);
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.priority - b.priority;
  });

  const displayItems = mergeHooksInOrder(sortedHooks, pendingAtPoint);
  const isEmpty = displayItems.length === 0;

  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", compact && "gap-1")}>
      <div className="flex items-center gap-1.5">
        <HookPointIcon
          className="shrink-0 text-muted-foreground"
          hookPoint={hookPoint}
        />
        <span
          className={cn(
            "truncate font-medium text-xs",
            compact && "text-[11px]"
          )}
        >
          {info.displayName}
        </span>
        <Badge
          className="shrink-0 px-1.5 py-0 text-[9px]"
          variant={displayItems.length > 0 ? "secondary" : "outline"}
        >
          {`${displayItems.length} hook${displayItems.length === 1 ? "" : "s"}`}
        </Badge>
      </div>

      {isEmpty ? (
        <div
          className={cn(
            "flex items-center gap-1.5 py-1 text-muted-foreground text-xs",
            compact && "text-[11px]"
          )}
        >
          <Check className="size-3 shrink-0" />
          <span>Pass through (no hooks)</span>
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-1.5">
          {displayItems.map((item, idx) => {
            const isLast = idx === displayItems.length - 1;
            if (item.type === "hook") {
              return (
                <HookNode
                  compact={compact}
                  hook={item.hook}
                  index={idx}
                  isHighlighted={highlightedPluginId === item.hook.plugin_id}
                  isLast={isLast}
                  key={item.hook.id}
                  onSelect={onSelectPlugin}
                />
              );
            }
            return (
              <PendingHookNode
                compact={compact}
                index={idx}
                isLast={isLast}
                key={`pending-${item.pending.hookPoint}-${item.pending.priority}`}
                pending={item.pending}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export const HookExecutionPipeline = ({
  hooks,
  pendingHooks,
  compact = false,
  highlightedPluginId,
  onSelectPlugin,
}: HookExecutionPipelineProps) => {
  if (compact) {
    return (
      <div className="flex flex-col gap-3">
        {HOOK_POINT_ORDER.map((hookPoint, idx) => (
          <div key={hookPoint}>
            <HookPointSection
              compact={compact}
              highlightedPluginId={highlightedPluginId}
              hookPoint={hookPoint}
              hooks={hooks.filter((h) => h.hook_point === hookPoint)}
              onSelectPlugin={onSelectPlugin}
              pendingHooks={pendingHooks}
            />
            {idx < HOOK_POINT_ORDER.length - 1 && (
              <div className="my-2 ml-6 flex items-center gap-2 text-muted-foreground">
                <ArrowDown className="size-3" />
                <span className="text-xs">then</span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between border-b px-6 py-4">
        <p className="text-muted-foreground text-sm">
          Each tool call passes through these stages. Hooks at each stage run in
          priority order.
        </p>
        {/* Legend */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="size-3 rounded-sm border-l-2 border-l-purple-500 bg-purple-500/20" />
            <span className="text-muted-foreground">Organization</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="size-3 rounded-sm border-l-2 border-l-pink-500 bg-pink-500/20" />
            <span className="text-muted-foreground">Project</span>
          </div>
        </div>
      </div>
      {/* Pipeline content */}
      <div className="overflow-x-auto p-6">
        {/* Horizontal pipeline flow - using grid for consistent column widths */}
        <div className="grid min-w-[1100px] grid-cols-[auto_24px_1fr_24px_1fr_24px_100px_24px_1fr_24px_100px] items-start gap-2 pb-4">
          {/* Step 1: Request Sources — Gateways, Clients, and Dashboard all feed into the pipeline */}
          <div className="flex flex-col items-center gap-1.5">
            <div className="flex items-center">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5 rounded-lg border-2 border-muted bg-muted/30 px-2.5 py-2">
                  <Globe className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="whitespace-nowrap font-medium text-xs">
                    Gateways
                  </span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg border-2 border-muted bg-muted/30 px-2.5 py-2">
                  <Monitor className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="whitespace-nowrap font-medium text-xs">
                    Clients
                  </span>
                </div>
                <div className="flex items-center gap-1.5 rounded-lg border-2 border-muted bg-muted/30 px-2.5 py-2">
                  <LayoutDashboard className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="whitespace-nowrap font-medium text-xs">
                    Dashboard
                  </span>
                </div>
              </div>
              {/* Converging bracket — merges the three sources into a single flow */}
              <div className="my-1 w-3 self-stretch rounded-r-lg border-muted-foreground/30 border-t-2 border-r-2 border-b-2" />
            </div>
          </div>

          <div className="flex justify-center pt-[50px]">
            <ArrowRight className="size-4 text-muted-foreground/60" />
          </div>

          {/* Step 2: Access Check Stage */}
          <div className="flex min-w-0 flex-col gap-2 overflow-hidden pt-[34px]">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-lg border-2 px-3 py-3",
                    STAGE_COLORS["tool.access"].border,
                    STAGE_COLORS["tool.access"].bg
                  )}
                >
                  <Eye
                    className={cn(
                      "size-4 shrink-0",
                      STAGE_COLORS["tool.access"].icon
                    )}
                  />
                  <span
                    className={cn(
                      "truncate font-medium text-sm",
                      STAGE_COLORS["tool.access"].text
                    )}
                  >
                    Access Check
                  </span>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground leading-tight">
                  Control which users can see and use each tool
                </p>
              </div>
            </div>
            <HookPointSection
              highlightedPluginId={highlightedPluginId}
              hookPoint="tool.access"
              hooks={hooks.filter((h) => h.hook_point === "tool.access")}
              onSelectPlugin={onSelectPlugin}
              pendingHooks={pendingHooks}
            />
          </div>

          <div className="flex justify-center pt-[50px]">
            <ArrowRight className="size-4 text-muted-foreground/60" />
          </div>

          {/* Step 3: Pre tool execution Stage */}
          <div className="flex min-w-0 flex-col gap-2 overflow-hidden pt-[34px]">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-lg border-2 px-3 py-3",
                    STAGE_COLORS["tool.pre"].border,
                    STAGE_COLORS["tool.pre"].bg
                  )}
                >
                  <Play
                    className={cn(
                      "size-4 shrink-0",
                      STAGE_COLORS["tool.pre"].icon
                    )}
                  />
                  <span
                    className={cn(
                      "truncate font-medium text-sm",
                      STAGE_COLORS["tool.pre"].text
                    )}
                  >
                    Pre tool execution
                  </span>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground leading-tight">
                  Validate inputs, check permissions, enrich data
                </p>
              </div>
            </div>
            <HookPointSection
              highlightedPluginId={highlightedPluginId}
              hookPoint="tool.pre"
              hooks={hooks.filter((h) => h.hook_point === "tool.pre")}
              onSelectPlugin={onSelectPlugin}
              pendingHooks={pendingHooks}
            />
          </div>

          <div className="flex justify-center pt-[50px]">
            <ArrowRight className="size-4 text-muted-foreground/60" />
          </div>

          {/* Step 4: Tool Execution Stage */}
          <div className="flex flex-col items-center gap-2 pt-[34px]">
            <div className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-muted bg-muted/30 px-3 py-3">
              <Terminal className="size-4 shrink-0 text-muted-foreground" />
              <span className="font-medium text-sm">Tool Runs</span>
            </div>
            <p className="text-center text-[11px] text-muted-foreground leading-tight">
              Executes with validated inputs
            </p>
          </div>

          <div className="flex justify-center pt-[50px]">
            <ArrowRight className="size-4 text-muted-foreground/60" />
          </div>

          {/* Step 5: Post tool execution Stage */}
          <div className="flex min-w-0 flex-col gap-2 overflow-hidden pt-[34px]">
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-lg border-2 px-3 py-3",
                    STAGE_COLORS["tool.post"].border,
                    STAGE_COLORS["tool.post"].bg
                  )}
                >
                  <Filter
                    className={cn(
                      "size-4 shrink-0",
                      STAGE_COLORS["tool.post"].icon
                    )}
                  />
                  <span
                    className={cn(
                      "truncate font-medium text-sm",
                      STAGE_COLORS["tool.post"].text
                    )}
                  >
                    Post tool execution
                  </span>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground leading-tight">
                  Redact sensitive data, filter or transform outputs
                </p>
              </div>
            </div>
            <HookPointSection
              highlightedPluginId={highlightedPluginId}
              hookPoint="tool.post"
              hooks={hooks.filter((h) => h.hook_point === "tool.post")}
              onSelectPlugin={onSelectPlugin}
              pendingHooks={pendingHooks}
            />
          </div>

          <div className="flex justify-center pt-[50px]">
            <ArrowRight className="size-4 text-muted-foreground/60" />
          </div>

          {/* Step 6: Response Stage */}
          <div className="flex flex-col items-center gap-2 pt-[34px]">
            <div className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-muted bg-muted/30 px-3 py-3">
              <Terminal className="size-4 shrink-0 text-muted-foreground" />
              <span className="font-medium text-sm">Response</span>
            </div>
            <p className="text-center text-[11px] text-muted-foreground leading-tight">
              Returned to caller
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
